import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import {
  Plus,
  Search,
  Edit,
  Copy,
  Trash,
  Power,
  Mail,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@caruso/ui';
import { emailTemplatesService } from '@/services/email-templates.service';

export function EmailTemplatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showInactive, setShowInactive] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['email-templates', { search, page, activeOnly: !showInactive }],
    queryFn: () =>
      emailTemplatesService.getAll({
        search,
        page,
        pageSize: 10,
        activeOnly: !showInactive,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }),
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      await emailTemplatesService.delete(id);
      toast({
        title: 'Template excluído',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao excluir template',
        variant: 'destructive',
      });
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await emailTemplatesService.toggle(id);
      toast({
        title: 'Status alterado',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao alterar status',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const newTemplate = await emailTemplatesService.duplicate(id);
      toast({
        title: 'Template duplicado',
        variant: 'success',
      });
      navigate(`/app/email-templates/${newTemplate.id}`);
    } catch (error) {
      toast({
        title: 'Erro ao duplicar template',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Templates de Email</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie seus templates de email personalizados
          </p>
        </div>
        <Button onClick={() => navigate('/app/email-templates/novo')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Novo Template
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome, assunto ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
          <Button
            variant={showInactive ? 'default' : 'outline'}
            onClick={() => setShowInactive(!showInactive)}
          >
            <Power className="w-4 h-4 mr-2" />
            {showInactive ? 'Mostrar Inativos' : 'Apenas Ativos'}
          </Button>
        </div>
      </Card>

      {/* Templates List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {data?.data.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
                  <div className="p-6" onClick={() => navigate(`/app/email-templates/${template.id}`)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-brand-gold/70" />
                          <h3 className="text-lg font-serif text-brand-gold">
                            {template.name}
                          </h3>
                          {!template.active && (
                            <span className="text-xs px-2 py-1 rounded bg-red-900/20 border border-red-500/30 text-red-400">
                              Inativo
                            </span>
                          )}
                          {template.category && (
                            <span className="text-xs px-2 py-1 rounded bg-brand-gold/10 border border-brand-gold/20 text-brand-gold">
                              {template.category}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-brand-ivory/80">
                            <span className="font-medium">Assunto:</span> {template.subject}
                          </p>
                          {template.description && (
                            <p className="text-sm text-brand-ivory/60">
                              {template.description}
                            </p>
                          )}
                        </div>
                        <div className="mt-3 text-xs text-brand-ivory/50">
                          Criado em {new Date(template.createdAt).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/app/email-templates/${template.id}`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuplicate(template.id)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggle(template.id)}
                          className={cn(
                            template.active
                              ? 'text-green-400 border-green-500/30'
                              : 'text-red-400 border-red-500/30'
                          )}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-brand-ivory/60">
                Mostrando {data.data.length} de {data.total} templates
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2 px-4">
                  <span className="text-sm text-brand-ivory">
                    Página {page} de {data.totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {data?.data.length === 0 && (
            <div className="text-center py-12">
              <Mail className="mx-auto h-12 w-12 text-brand-ivory/30" />
              <h3 className="mt-4 text-lg font-medium text-brand-ivory">
                Nenhum template encontrado
              </h3>
              <p className="mt-2 text-sm text-brand-ivory/60">
                Comece criando um novo template de email.
              </p>
              <Button
                onClick={() => navigate('/app/email-templates/novo')}
                variant="premium"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Template
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
